// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser, user } = useAuth();
  const hasProcessed = useRef(false);
  const [pendingNav, setPendingNav] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/login", { replace: true });
      return;
    }
    const session_id = decodeURIComponent(m[1]);
    const targetPath = window.location.pathname && window.location.pathname !== "/" ? window.location.pathname : "/dashboard";

    (async () => {
      try {
        const r = await api.post("/auth/session", { session_id });
        setUser(r.data);
        // If they came in via a ?ref= link before signing up, attach it now.
        try {
          const { getStoredReferralCode, clearStoredReferralCode } = await import("../lib/referral");
          const refCode = getStoredReferralCode();
          if (refCode) {
            await api.post("/me/referral/attach", { code: refCode }).catch(() => { /* silent */ });
            clearStoredReferralCode();
          }
        } catch (_) { /* ignore */ }
        // Clean the URL, then queue navigation. The second useEffect below
        // will fire only AFTER React commits the user state, eliminating the
        // race where ProtectedRoute sees user=null and bounces us to /login.
        window.history.replaceState(null, "", targetPath);
        setPendingNav(targetPath);
      } catch (e) {
        // Surface a short error code so we can diagnose mobile-only sign-in failures.
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        const code = status ? `${status}` : (e?.message || "network");
        setFailed(`${code}${detail ? ` · ${detail}` : ""}`);
        hasProcessed.current = false; // allow retry
      }
    })();
  }, [navigate, setUser]);

  // Navigate ONLY after user state is committed.
  useEffect(() => {
    if (pendingNav && user) {
      navigate(pendingNav, { replace: true });
    }
  }, [pendingNav, user, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] p-6">
      <div className="nb-card p-8 max-w-sm text-center" style={{ background: "#FDE047" }}>
        {failed ? (
          <>
            <p className="font-display text-lg font-bold">Sign-in didn't go through.</p>
            <p className="text-sm text-slate-700 mt-2">Tap below to try again.</p>
            {typeof failed === "string" && (
              <p className="text-[10px] font-mono text-slate-500 mt-2 break-all" data-testid="auth-error-code">err: {failed}</p>
            )}
            <button
              data-testid="auth-retry-btn"
              onClick={() => { window.location.href = "/login"; }}
              className="nb-btn-primary mt-4 w-full"
            >Try again</button>
          </>
        ) : (
          <p className="font-display text-xl font-bold">Signing you in…</p>
        )}
      </div>
    </div>
  );
}
